import { Outlet, useLocation } from 'react-router-dom';
import { AppHeader } from './app-header.js';
import { AppNav } from './app-nav.js';
import { AthleteNav } from './athlete-nav.js';
import { VideoSendStrip } from './video-send-strip.js';
import { ErrorBoundary } from './error-boundary.js';
import { ProfileGate } from './profile-gate.js';
import { PracticeOutboxSender } from './practice-outbox-sender.js';
import { ReleasedBanner } from './released-banner.js';
import { CartStrip } from './cart-strip.js';
import { MeProvider, useMeContext } from '../api/me-provider.js';
import { CartProvider } from '../api/cart-provider.js';
import { AthleteHomeRoute } from '../routes/athlete-home-route.js';
import { AthleteAccountRoute } from '../routes/athlete-account-route.js';
import { ToolsRoute } from '../routes/tools-route.js';

/**
 * The frame every route renders inside. Content sits on --plane and the page
 * ground (--void) shows only around the frame on a wide screen, which is how the
 * mockups are composed.
 *
 * TWO FRAMES SINCE 16 OCTOBER. /api/me says whether this is an athlete; if so
 * the athlete's frame renders instead, with the same header and none of the
 * adult machinery. Decided here, once, because MeProvider has to be above it.
 */
export function AppLayout() {
  return (
    <MeProvider>
      <PracticeOutboxSender />
      <Frame />
    </MeProvider>
  );
}

function Frame() {
  const { state } = useMeContext();
  const { pathname } = useLocation();
  // Recul is the whole screen (ruled 24 October): no header, no tabs, for
  // anyone - its own ‹ is the way back.
  if (pathname.endsWith('/recul')) return <Outlet />;
  // The Done screen follows it the same way (board R3), athletes included.
  if (pathname.startsWith('/tools/practice/')) return <Outlet />;
  if (state.status === 'athlete') return <AthleteFrame />;
  if (state.status === 'athlete-off') return <TurnedOffFrame />;
  // A second parent the first removed: their own screen (ruled 17 October) -
  // except on /join, so a new invitation can bring them back.
  if (state.status === 'ready' && state.me.removed_from_family && pathname !== '/join') return <RemovedParentFrame />;
  return <AdultFrame />;
}

/**
 * Parents and coaches.
 *
 * The boundary is INSIDE the frame on purpose: a route that throws should leave
 * the header and navigation intact so the person can get somewhere else, rather
 * than blanking the app.
 *
 * MeProvider wraps the whole frame because the header needs the customer's name
 * and the gate needs their profile, and those must be the same answer from the
 * same request. CartProvider is there for the same reason and one more: the
 * released-hours banner lives in the frame rather than on a screen, so the frame
 * and the cart screen have to be reading one cart, not two.
 *
 * The gate sits inside the boundary and around the outlet, so an incomplete
 * profile replaces the ROUTE and nothing else. Header, sign-out and navigation
 * all still render — which is what makes gating every route safe rather than a
 * trap.
 *
 * The banner is inside the gate: somebody who has not finished signing up has no
 * cart to have lost, and a warn-toned banner over the profile form would be the
 * first thing they ever saw of this app.
 */
function AdultFrame() {
  const { state } = useMeContext();
  const { pathname } = useLocation();
  // A sign-up link opened signed out has one way forward, its own Sign up
  // (ruled 24 October, after a parent left the page by the tabs, signed in
  // from there and got a second, empty family). So no tabs on it.
  const linkSignedOut = state.status === 'signed-out' && (pathname === '/welcome' || pathname === '/join');
  return (
    <CartProvider>
      <div className="app-frame">
        <AppHeader />
        <main className="app-main">
          <ErrorBoundary>
            <ProfileGate>
              <ReleasedBanner />
              <Outlet />
            </ProfileGate>
          </ErrorBoundary>
        </main>
        {/* One sticky foot, so the strip rides on the tabs rather than
            guessing their height. */}
        {linkSignedOut ? null : (
          <div className="app-foot">
            <VideoSendStrip />
            <CartStrip />
            <AppNav />
          </div>
        )}
      </div>
    </CartProvider>
  );
}

/**
 * An athlete (Athlete Portal canvas, rows 3 and 6, approved 16 October). No
 * cart - the API would refuse it - no profile form, no released-hours banner.
 *
 * The tabs each draw the athlete's own screen, chosen HERE from the address
 * rather than by the route table: / and /account are the parent's screens there,
 * and neither may render for a child. /my and /my/:lessonId are theirs and go through the outlet. Any other
 * address lands on Home (ruled 16 October), never an adult screen that could
 * only fail.
 */
function AthleteFrame() {
  return (
    <div className="app-frame">
      <AppHeader />
      <main className="app-main">
        <ErrorBoundary>
          <AthleteScreen />
        </ErrorBoundary>
      </main>
      <div className="app-foot">
        <VideoSendStrip />
        <AthleteNav />
      </div>
    </div>
  );
}

function AthleteScreen() {
  const { pathname } = useLocation();
  if (pathname === '/my' || pathname.startsWith('/my/')) return <Outlet />;
  if (pathname === '/signature' || pathname.startsWith('/signature/')) return <Outlet />;
  if (pathname === '/practices' || pathname.startsWith('/practices/')) return <Outlet />;
  if (pathname === '/tools') return <ToolsRoute />;
  if (pathname === '/account') return <AthleteAccountRoute />;
  return <AthleteHomeRoute />;
}

/**
 * An athlete whose access their parent deactivated ("Noa · sign-in turned off",
 * approved 16 October; reworded by Matthew 17 October): the header, so they can
 * sign out, one sentence, and no tabs - there is nowhere to go.
 */
function TurnedOffFrame() {
  return (
    <div className="app-frame">
      <AppHeader />
      <main className="app-main">
        <section className="panel">
          <h2>Your access has been removed or your account has been deactivated</h2>
        </section>
      </main>
    </div>
  );
}

/**
 * A second parent the first removed from the family (canvas row D; Matthew's
 * words, 17 October). The same frame as an athlete's, saying who to ask.
 */
function RemovedParentFrame() {
  return (
    <div className="app-frame">
      <AppHeader />
      <main className="app-main">
        <section className="panel">
          <h2>You no longer have access to this account</h2>
          <p>Contact the account owner to regain access.</p>
        </section>
      </main>
    </div>
  );
}
